import { useEffect, useMemo, useState } from 'react';
import { addMonths } from 'date-fns/fp';
import { getCalendars } from './utils';
import { getDateProps, getBackProps, getForwardProps } from './get-render-props';
import { useFocusRoving } from './use-focus-roving';
import { getOffset, determineSelectedDates, addToTabSequence, isOffsetControlled } from './helpers';
export function useCalendar({ date, maxDate, minDate, monthsToDisplay = 1, weekStartsOn = 0, showOutsideDays = false, offset, onDateSelected, onOffsetChanged, selectedDates, ref, manageFocus = false, show, }) {
    const [stateOffset, setStateOffset] = useState(0);
    const offsetMonth = getOffset(offset, stateOffset);
    const [focus, setFocus] = useState(true);
    let realRef = ref;
    if (!realRef && typeof window !== 'undefined')
        realRef = window.document;
    const { focusedDay, handleKeyDown, stateOffset: newStateOffset, setFocusedDay, } = useFocusRoving({
        initialFocusedDay: date,
        weekStartsOn,
        initOffset: offsetMonth,
    });
    const selected = useMemo(() => determineSelectedDates(selectedDates), [selectedDates]);
    function generateCalendars() {
        return getCalendars({
            date,
            selected,
            monthsToDisplay,
            minDate,
            maxDate,
            offset: offsetMonth,
            weekStartsOn,
            showOutsideDays,
        });
    }
    const calendars = useMemo(generateCalendars, [
        selected,
        monthsToDisplay,
        minDate,
        maxDate,
        offsetMonth,
        weekStartsOn,
        showOutsideDays,
        date,
    ]);
    useEffect(() => {
        setStateOffset(newStateOffset);
    }, [newStateOffset]);
    useEffect(() => {
        addToTabSequence(realRef || document, focusedDay, focus);
    }, [calendars]);
    useEffect(() => {
        setStateOffset(0);
    }, [date]);
    useEffect(() => {
        addToTabSequence(realRef || document, focusedDay, focus);
    }, [realRef, focusedDay, show]);
    function evaluateProps() {
        if (typeof onDateSelected !== 'function') {
            throw new Error('onDateSelected ist required!');
        }
    }
    useEffect(evaluateProps, [onDateSelected]);
    function handleOffsetChanged(newOffset) {
        if (!isOffsetControlled(offset)) {
            setStateOffset(newOffset);
            const focusDay = addMonths(newOffset - stateOffset)(focusedDay);
            setFocusedDay(focusDay);
        }
        if (typeof onOffsetChanged === 'function') {
            onOffsetChanged(newOffset);
        }
    }
    function onBlured() {
        setFocus(false);
    }
    function onFocused() {
        setFocus(true);
    }
    return {
        calendars,
        getDateProps: getDateProps.bind(null, {
            onDateSelected,
            handleKeyDown,
            manageFocus,
            onBlured,
            onFocused,
            date,
        }),
        getBackProps: getBackProps.bind(null, {
            minDate,
            offsetMonth,
            handleOffsetChanged,
        }),
        getForwardProps: getForwardProps.bind(null, {
            maxDate,
            offsetMonth,
            handleOffsetChanged,
        }),
    };
}
